import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box,
  Button,
  useToast,
  Radio,
  RadioGroup,
  Stack,
  Text
} from '@chakra-ui/react';
import { getLibrariesData, updateLibrariesHighlightData, updateHighlightData } from '../../../services/Libraries';
import { getPersonalityAliasSuggestions } from '../../../services/Personality';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import TypeaheadWidget from '../../../CommonComponents/TypeaheadWidget';
import FormLoader from '../../../CommonComponents/FormLoader';
import { handleApiError, showSuccessToast, showWarningToast, showErrorToast } from '../../../util/errorHandler';

const Highlights = () => {

  const { alias } = useParams();
  const [quickSave, setQuickSave] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [highlightAll, setHighlightAll] = useState(''); // 'yes', 'no', or ''
  const [fetchedData, setFetchedData] = useState(null); // Store fetched data to restore when switching back
  const toast = useToast();

  //Need to add the type of research-centre, library, archive, etc.
 //take it from the url
  const type=window.location.pathname.split('/')[1];

  // Form change handler
  const handleFormChange = (data) => {
    setFormData(data.formData);
  };

  // Handle radio button change
  const handleRadioChange = (value) => {
    setHighlightAll(value);
    // If "yes" is selected, clear the form data
    if (value === 'yes') {
      setFormData({ highlight: [] });
    } else if (value === 'no') {
      // Restore fetched data if available, otherwise initialize with empty highlight array
      if (fetchedData) {
        setFormData({
          ...fetchedData,
          highlight: fetchedData.highlight || fetchedData.keywords || []
        });
      } else if (!formData.highlight) {
        setFormData({ highlight: [] });
      }
    }
  };

  // Schema for highlight field
  const schema = {
    "description": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "highlight": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Highlight"
      }
    }
  };

  // UI schema
  const uiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    highlight: {
      "ui:emptyValue": [],
      items: {
        "ui:widget": "personalityTypeahead",
      },
      "ui:options": {
        addable: true,
        removable: true,
        orderable: true
      }
    }
  };

  // Fetch personality alias suggestions for typeahead
  const fetchPersonalityAliasSuggestions = async (inputValue) => {
    try {
      const result = await getPersonalityAliasSuggestions(20, 1, inputValue);
      const suggestions = result?.data?.data || [];
      
      return suggestions.map((item) => ({
        label: item.alias || item,
        value: item.alias || item
      }));
    } catch (error) {
      console.error('Error fetching personality suggestions:', error);
      return [];
    }
  };

  // Widgets configuration
  const widgets = {
    personalityTypeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={fetchPersonalityAliasSuggestions}
      />
    ),
  };

  // Fetch initial data
  useEffect(() => {
    const getHighlightData = async () => {
      try {
        if (alias) {
          const result = await getLibrariesData(alias, 'highlight', type);
          if (result) {
            // Handle both array and object with data property
            const data = result?.data?.[0] || result?.[0] || {};
            
            // Store the fetched data to restore later when switching back to "no"
            const highlightArray = data?.highlight || [];
            setFetchedData({
              ...data,
              highlight: highlightArray
            });
            
            // Set radio button based on highlight data length and values
            const highlightLength = highlightArray?.length;
            // Check if array contains 'yes' (case-insensitive)
            const hasYes = highlightArray?.some(item => 
              String(item).toLowerCase() === 'yes'
            );
            
            if (highlightLength === 0) {
              // If array length is zero, both radio buttons should be unselected
              setHighlightAll('');
            } else if (highlightLength === 1 && hasYes) {
              // If length is 1 and value is 'yes', select 'yes' radio button
              setHighlightAll('yes');
            } else if (highlightLength > 1 && !hasYes) {
              // If length is more than 1 and doesn't have 'yes', select 'No' radio button
              setHighlightAll('no');
            } else {
              // Default: no radio button selected
              setHighlightAll('');
            }
            
            // Set form data with highlight (or empty array)
            setFormData({
              ...data,
              highlight: highlightArray
            });
            setQuickSave(false);
          } else {
            // Initialize with empty highlight array if no data exists
            const emptyData = { highlight: [] };
            setFetchedData(emptyData);
            setFormData(emptyData);
            setHighlightAll('');
          }
        } else {
          // For new records, initialize empty form
          const emptyData = { highlight: [] };
          setFetchedData(emptyData);
          setFormData(emptyData);
          setHighlightAll('');
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch highlight data"
        });
      } finally {
        setIsLoading(false);
      }
    };

    getHighlightData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      if (!alias) {
        showErrorToast(toast, "Alias is required");
        return;
      }

      // Scenario 1: "Yes" is selected - highlight all keywords
      if (highlightAll === 'yes') {
        const result = await updateHighlightData(alias, 'yes', type);
        
        if (result?.status === 200) {
          // Update fetchedData to reflect the saved state
          setFetchedData({ highlight: [], highlight_all: 'yes' });
          setQuickSave(prev => !prev);
          showSuccessToast(toast, "Highlight data updated successfully");
        }
      }
      // Scenario 2: "No" is selected
      else if (highlightAll === 'no') {
        // Ensure highlight is an array
        const highlightArray = Array.isArray(formData.highlight) ? formData.highlight : [];
        
        // Scenario 2a: Highlight array is empty - highlight=no
        if (highlightArray.length === 0) {
          const result = await updateHighlightData(alias, 'no', type);
          
          if (result?.status === 200) {
            // Update fetchedData to reflect the saved state
            setFetchedData({ highlight: [], highlight_all: 'no' });
            setQuickSave(prev => !prev);
            showSuccessToast(toast, "Highlight data updated successfully");
          }
        }
        // Scenario 2b: Highlight array has values - highlight=yes with keywords payload
        else {
          // Transform highlight to keywords in the API payload
          const payload = { keywords: highlightArray };
          const result = await updateLibrariesHighlightData(alias, payload, type);
          
          if (result?.status === 200) {
            // Update fetchedData to reflect the saved state
            setFetchedData({
              ...formData,
              highlight: highlightArray,
              highlight_all: 'no'
            });
            setQuickSave(prev => !prev);
            showSuccessToast(toast, "Highlight data updated successfully");
          }
        }
      }
      // Scenario 3: No value selected
      else {
        showWarningToast(toast, "Please select whether you want to highlight for all keywords");
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update highlight data"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
            <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
              <Box fontSize="lg" fontWeight="bold" mb={4}>Highlights</Box>
              
              {/* Radio Button Question */}
              <Box mb={6}>
                <Text fontSize="md" fontWeight="medium" mb={3}>
                  Do you want to highlight for all keywords?
                </Text>
                <RadioGroup onChange={handleRadioChange} value={highlightAll}>
                  <Stack direction="row" spacing={4}>
                    <Radio value="yes">Yes</Radio>
                    <Radio value="no">No</Radio>
                  </Stack>
                </RadioGroup>
              </Box>

              {/* Show form only if "No" is selected */}
              {highlightAll === 'no' && (
                <Box mt={4}>
                  <Form
                    schema={schema}
                    uiSchema={uiSchema}
                    formData={formData}
                    validator={validator}
                    onChange={handleFormChange}
                    widgets={widgets}
                    fontsize={"14px"}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Highlights;
